import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { appActions } from '../../../store';
import { useUrlSync } from '../../../hooks/useUrlSync';
import { useActiveProfile } from '../../../hooks/useActiveProfile';
import ProductList from '../../shared/ProductList/ProductList';
import ColorSlotSelector from './ColorSlotSelector';
import styles from "./ProductMenu.module.css";

export default function ProductMenu() {
    const [activeSection, setActiveSection] = useState('style'); // 'style', 'size', or 'base-accent'
    const dispatch = useDispatch();
    
    // Sync Redux state with URL
    useUrlSync();
    
    // Fetch products and set active profile based on style/size
    useActiveProfile();
    
    // Get current selections from Redux
    const style = useSelector(state => state.app.style);
    const size = useSelector(state => state.app.size);
    const activeProfile = useSelector(state => state.app.activeProfile);

    // Generate swatch image URL for style card (without products applied)
    const getStyleCardImage = () => {
        if (!activeProfile || !activeProfile.description) return null;
        
        // Use decalID from description field
        const decalID = activeProfile.description;
        const slots = activeProfile.descriptor ? activeProfile.descriptor.split(',').length : 3;
        const selList = Array(slots).fill(-1).join(',');
        
        return `https://renderfarm2.chameleonpower.com/proxy/getImage.ashx?specifier=!${decalID}&sel=${selList}&size=200&extra=(DESIGN=(MODEL=TILE),focus=(@size[x]/@res[x]/1in),size=(?,(@size[x]*@res[y]/@res[x])))`;
    };

    const styleCardImage = getStyleCardImage();

    // Get size card image
    const getSizeCardImage = () => {
        if (!size) return null;
        return `/img/${size}.jpg`;
    };

    const sizeCardImage = getSizeCardImage();

    const handleSectionClick = (section) => {
        setActiveSection(section);
    };

    const handleStyleSelect = (product) => {
        // Reset product selections when style changes
        dispatch(appActions.setProductSelection([-1, -1]));
        
        // Switch back to swatch view if currently on room view
        dispatch(appActions.setCurrentView('swatch'));
        
        // Update style in Redux when a product is selected
        dispatch(appActions.setStyle(product.name || product.color || 'Selected'));
        console.log('Style selected:', product);
        console.log('Product selections reset');
        console.log('Switched to swatch view');
    };

    return (
        <section className={styles.productMenu}>
            <div className={styles.header}>
                <h1>AMTICO ACTIVE LINES CUSTOM</h1>
            </div>
            {/* <div className={styles.subheader}>
                <h2>Explore custom options</h2>
            </div> */}
            <div className={styles.disclaimer}>Minimum order: 500 sq ft</div>

            <div className={styles.selectionGrid}>
                {/* Style Section */}
                <div 
                    className={`${styles.selectionCard} ${activeSection === 'style' ? styles.active : ''}`}
                    onClick={() => handleSectionClick('style')}
                    title="Select a style pattern"
                >
                    <div className={styles.cardImage}>
                        {styleCardImage ? (
                            <img 
                                src={styleCardImage} 
                                alt={`${style} swatch`}
                                className={styles.cardImageImg}
                            />
                        ) : (
                            <div className={styles.stylePlaceholder}></div>
                        )}
                    </div>
                    <div className={styles.cardLabel}>Style</div>
                    <div className={styles.cardValue}>{style || 'Select'}</div>
                </div>

                {/* Size Section */}
                <div 
                    className={`${styles.selectionCard} ${activeSection === 'size' ? styles.active : ''}`}
                    onClick={() => handleSectionClick('size')}
                    title="Select tile or plank size"
                >
                    <div className={styles.cardImage}>
                        {sizeCardImage ? (
                            <img 
                                src={sizeCardImage} 
                                alt={`${size} size`}
                                className={styles.cardImageImg}
                            />
                        ) : (
                            <div className={styles.sizePlaceholder}></div>
                        )}
                    </div>
                    <div className={styles.cardLabel}>Size</div>
                    <div className={styles.cardValue}>{size || '18x18'}</div>
                </div>

                {/* Base + Accent Section */}
                <div 
                    className={`${styles.selectionCard} ${activeSection === 'base-accent' ? styles.active : ''}`}
                    onClick={() => handleSectionClick('base-accent')}
                    title="Choose base and accent colors"
                >
                    <div className={styles.cardImage}>
                        <div className={styles.baseAccentPlaceholder}>
                                                                <img 
                                        src="/img/brush.svg" 
                                        alt="Choose a color"
                                        className={styles.sizeImage}
                                    />
                        </div>
                    </div>
                    <div className={styles.cardLabel}>Base + Accent</div>
                    <div className={styles.cardValue}>Choose a color</div>
                </div>
            </div>

            {/* Content Area - Shows different content based on active section */}
            <div className={styles.contentArea}>
                {activeSection === 'style' && (
                    <div className={styles.styleContent}>
                        <ProductList 
                            seriesId="9700"
                            thumbSize="256"
                            titleAttribute="name"
                            descriptionAttribute=""
                            filters={{ color: "18x18" }}
                            onProductSelect={handleStyleSelect}
                            gridClassName={styles.twoColumnGrid}
                        />
                    </div>
                )}

                {activeSection === 'size' && (
                    <div className={styles.sizeContent}>
                        <div className={styles.sizeOptions}>
                            <div 
                                className={`${styles.sizeOption} ${size === '18x18' ? styles.selectedSize : ''}`}
                                onClick={() => dispatch(appActions.setSize('18x18'))}
                            >
                                <div className={styles.sizeIcon}>
                                    <img 
                                        src="/img/18x18.jpg" 
                                        alt="18x18 tile"
                                        className={styles.sizeImage}
                                    />
                                </div>
                                <div className={styles.sizeLabel}>18x18in tile</div>
                            </div>

                            <div 
                                className={`${styles.sizeOption} ${size === '6x36' ? styles.selectedSize : ''}`}
                                onClick={() => dispatch(appActions.setSize('6x36'))}
                            >
                                <div className={styles.sizeIcon}>
                                    <img 
                                        src="/img/6x36.jpg" 
                                        alt="6x36 plank"
                                        className={styles.sizeImage}
                                    />
                                </div>
                                <div className={styles.sizeLabel}>6x36in plank</div>
                            </div>
                        </div>
                    </div>
                )}

                {activeSection === 'base-accent' && (
                    <div className={styles.baseAccentContent}>
                        {activeProfile && activeProfile.descriptor ? (
                            <ColorSlotSelector 
                                descriptor={activeProfile.descriptor}
                                activeProfile={activeProfile}
                            />
                        ) : (
                            <div className={styles.noProfileMessage}>
                                Please select a style and size first
                            </div>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}